import React, { useState, useEffect } from "react";
import { Table, Tag, Select, Skeleton, Button, Space, Typography } from "antd";
import { TableProps } from "antd";
import {
    CheckCircleOutlined,
    StopOutlined,
    PauseCircleOutlined,
    PoweroffOutlined,
    SyncOutlined,
    AccountBookOutlined
} from '@ant-design/icons';
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import coreAPIService from "../apiServices/coreAPIService";
import { CTW_API_ENDPOINT } from "../utils";
import { useNavigate } from 'react-router-dom';
import { useMetaSetup } from '../context/MetaSetupContext';
import { useAuthOrg } from '../context/AuthOrgContext';
import { CampaignApiResponse, Record } from "../interfaces/campaign";
import { toast } from "../common/Popover";
import Heading from "../common/Heading";
import CommonLayout from "../common/LayoutBlock";
import '../assets/css/tablesShadow.css'

const { Title, Text } = Typography;
const { Option } = Select;

interface AdAccount {
    id: string;
    name: string;
    accountStatus: string;
    isDefault: string;
}

interface AccountDetails {
    adAccountName: string | null;
    adAccountId: string | null;
    adAccountStatus: string | null;
    isDefault: string | null;
}

interface ApiResponse {
    meta: { status: string; code: number; message: string };
    data?: any;
}

const skeletonBlockStyle = { height: "200px", width: "100%" };

const CampaignListing: React.FC = () => {
    const { setBreadcrumbs } = useBreadcrumbs();
    const navigate = useNavigate();
    const { selectedAdAccount, setSelectedAdAccount, metaConnectStatus } = useMetaSetup();
    const { currentOrg, availableOrgs, switchOrg } = useAuthOrg();

    // State
    const [adAccounts, setAdAccounts] = useState<AdAccount[]>([]);
    const [filteredAdAccounts, setFilteredAdAccounts] = useState<AdAccount[]>([]);
    const [accountDetails, setAccountDetails] = useState<AccountDetails | null>(null);
    const [campaigns, setCampaigns] = useState<Record[]>([]);
    const [filteredCampaigns, setFilteredCampaigns] = useState<Record[]>([]);
    const [searchData, setSearchData] = useState<Record[]>([]);
    const [loadingAccounts, setLoadingAccounts] = useState(true);
    const [loadingCampaigns, setLoadingCampaigns] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [searchValue, setSearchValue] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string>("ALL");

    // Breadcrumbs
    useEffect(() => {
        setBreadcrumbs([
            {
                title: <div className="text-lg cursor-pointer" onClick={() => navigate("/ctc-ads-meta/ad-accounts")}>
                    Meta Ad Accounts
                </div>,
            },
        ]);
    }, [setBreadcrumbs, navigate]);

    // Fetch Ad Accounts (on mount)
    useEffect(() => {
        const fetchAdAccounts = async () => {
            setLoadingAccounts(true);
            try {
                const res = await coreAPIService.get<ApiResponse>(CTW_API_ENDPOINT, 'v2/org/ad-accounts');
                if (res.meta.code === 200 && res.data?.length > 0) {
                    const accounts: AdAccount[] = res.data
                        .filter((acc: any) => acc.accountStatus === 1)
                        .map((acc: any) => ({
                            id: acc.id,
                            name: acc.name,
                            accountStatus: acc.accountStatus || "Unknown",
                            isDefault: acc.isDefault || "Unknown",
                        }));
                    setAdAccounts(accounts);
                    setFilteredAdAccounts(accounts);

                    // Auto-select first account if none selected in context
                    if (accounts.length > 0 && !selectedAdAccount) {
                        setSelectedAdAccount({ id: accounts[0].id, name: accounts[0].name });
                    }
                } else {
                    toast("warning", "No ad accounts found");
                }
            } catch (error) {
                toast("error", "Failed to load ad accounts");
            } finally {
                setLoadingAccounts(false);
            }
        };

        fetchAdAccounts();
    }, []);

    // Fetch Campaigns when account changes
    useEffect(() => {
        if (!selectedAdAccount?.id) return;

        const fetchCampaigns = async () => {
            setLoadingCampaigns(true);
            try {
                const res = await coreAPIService.get<CampaignApiResponse>(
                    CTW_API_ENDPOINT,
                    `v2/org/adaccount-campaign/${selectedAdAccount?.id}`
                );

                if (res.meta.code === 200) {
                    const camps: Record[] = res.data || [];
                    setCampaigns(camps);
                    setSearchData(camps);
                    applyFilters(camps, searchValue, statusFilter);
                } else {
                    toast("warning", "No campaigns found");
                }
            } catch (error) {
                toast("error", "Failed to load campaigns");
            } finally {
                setLoadingCampaigns(false);
            }
        };

        fetchCampaigns();
    }, [selectedAdAccount?.id]);

    // Sync Campaigns for selected account
    const syncCampaigns = async () => {
        if (!selectedAdAccount?.id) return;
        setSyncing(true);
        try {
            const res = await coreAPIService.post<{ meta: { code: number, message: string } }>(
                CTW_API_ENDPOINT,
                `v2/meta-integration/adaccounts/${selectedAdAccount?.id}/ctwa-campaigns/sync`
            );
            if (res.meta.code === 200) {
                toast("success", "Campaigns synced successfully");
                // Refresh campaigns
                const campRes = await coreAPIService.get<CampaignApiResponse>(
                    CTW_API_ENDPOINT,
                    `v2/org/adaccount-campaign/${selectedAdAccount?.id}`
                );
                if (campRes.meta.code === 200) {
                    const camps = campRes.data || [];
                    setCampaigns(camps);
                    setSearchData(camps);
                    applyFilters(camps, searchValue, statusFilter);
                }
            } else {
                toast("warning", "Sync failed");
            }
        } catch (error) {
            toast("error", "Sync error");
        } finally {
            setSyncing(false);
        }
    };

    // Handle Account Change
    const handleAccountChange = (value: string) => {
        const account = adAccounts.find(acc => acc.id === value);
        if (account) {
            setSelectedAdAccount({ id: account.id, name: account.name });
        }
        setSearchValue(null);
        setStatusFilter("ALL");
    };

    // Filters
    const applyFilters = (data: Record[], search: string | null, status: string) => {
        let filtered = [...data];

        if (search) {
            filtered = filtered.filter(c =>
                c.campaignName?.toLowerCase().includes(search.toLowerCase())
            );
        }

        if (status !== "ALL") {
            filtered = filtered.filter(c =>
                c.status?.toLowerCase() === status.toLowerCase()
            );
        }

        setFilteredCampaigns(filtered);
    };

    const handleSearch = (value: string) => {

        setSearchValue(value);
        applyFilters(campaigns, value, "ALL");
    };

    const handleStatusChange = (value: string) => {
        setStatusFilter(value);
        applyFilters(campaigns, searchValue, value);
    };

    const handleSwitchChange = (checked: boolean, record: Record) => {
        const updated = campaigns.map(c =>
            c.campaignId === record.campaignId ? { ...c, enableButton: checked } : c
        );
        setCampaigns(updated);
        applyFilters(updated, searchValue, statusFilter);
    };

    const handleDelete = async (campaignId: string) => {
        if (!window.confirm("Are you sure you want to delete this campaign?")) return;

        try {
            const res = await coreAPIService.delete<{ meta: { code: number, message: string } }>(
                CTW_API_ENDPOINT,
                `v2/campaigns/${campaignId}`
            );
            if (res.meta.code === 200) {
                toast("success", "Campaign deleted successfully");
                // Refresh
                const updated = campaigns.filter(c => c.campaignId !== campaignId);
                setCampaigns(updated);
                applyFilters(updated, searchValue, statusFilter);
            } else {
                toast("error", res.meta.message || "Failed to delete campaign");
            }
        } catch (error) {
            toast("error", "Failed to delete campaign");
        }
    };
    // objective: string
    // buyingType: string
    // dailyBudget: string
    // lifetimeBudget: string
    // budgetRemaining: string
    // spend: string
    // Table Columns
    const columns: TableProps<Record>['columns'] = [
        {
            title: "Account",
            dataIndex: "orgName",
            key: "orgName",
            render: (_: any, record: Record) => (
                <Tag color="blue">{record.orgName.length > 30 ? record.orgName.substring(0, 30) + "..." : record.orgName || "Unknown"}</Tag>
            ),
            width: 150,
            fixed: 'left',
        },
        {
            title: "Campaign Name",
            dataIndex: "name",
            key: "name",
            render: (_: any, record: Record) => (
                <div>
                    <Space>
                        <div className="font-medium">{record.campaignName}</div>
                        {record.isTimesPanelCampaign && (
                            <Tag color="purple" className="m-0 text-[10px] px-1 leading-4 uppercase font-bold">
                                Panel
                            </Tag>
                        )}
                    </Space>
                    <div className="text-xs text-blue-600">
                        <a onClick={() => navigate(`/ctc-ads-meta/ad-accounts/adsets/${record.campaignId}`, { state: { campaignName: record.campaignName, isTimesPanelCampaign: record.isTimesPanelCampaign } })}>View AdSets</a>
                    </div>
                </div>
            ),
            width: 250,
            fixed: 'left',
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (_: any, record: Record) => {
                const status = record.status?.toLowerCase();
                let color = "default";
                let icon = <PoweroffOutlined />;

                if (status === "active") { color = "green"; icon = <CheckCircleOutlined />; }
                else if (status === "paused") { color = "orange"; icon = <PauseCircleOutlined />; }
                else if (status === "inactive") { color = "red"; icon = <StopOutlined />; }
                else if (status === "disabled") { color = "gray"; icon = <PoweroffOutlined />; }

                return <Tag icon={icon} color={color}>{record.status}</Tag>;
            },
            align: "center",
            width: 120,
        },
        {
            title: "Objective",
            dataIndex: "objective",
            key: "objective",
            render: (_: any, record: Record) => record.objective?.split("_")[1] || "-",
            align: "center",
            width: 120,
        },
        // {
        //     title: "Lifetime Budget",
        //     dataIndex: "lifetimeBudget",
        //     key: "lifetimeBudget",
        //     render: (_: any, record: Record) => record.lifetimeBudget ? `₹ ${Number(record.lifetimeBudget).toLocaleString()}` : "-",
        //     align: "center",
        //     width: 150,
        // },
        {
            title: "Start Date",
            dataIndex: "startDate",
            key: "startDate",
            render: (_: any, record: Record) => record.startTime?.split("T")?.[0] || "-",
            align: "center",
            width: 120,
        },
        {
            title: "End Date",
            dataIndex: "endDate",
            key: "endDate",
            render: (_: any, record: Record) => record.stopTime?.split("T")?.[0] || "-",
            align: "center",
            width: 120,
        },
        {
            title: "Spends",
            dataIndex: "spend",
            key: "spend",
            render: (_: any, record: Record) => record.spend ? `₹ ${Number(record.spend).toLocaleString()}` : "-",
            align: "center",
            width: 120,
        },
    ];

    return (
        <CommonLayout>
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <Heading title="Campaigns" className="m-0" />
                {/* Ad Account Selector */}
                <div className="flex gap-2 mb-6">
                    {/* <Space direction="horizontal" className="w-full">
                        <Text strong><GlobalOutlined /> Account</Text>
                        <Select
                            value={currentOrg?.orgId}
                            onChange={switchOrg}
                            style={{ width: 200 }}
                            placeholder="Select Account"
                        >
                            {availableOrgs.map((org) => (
                                <Option key={org.orgId} value={org.orgId}>
                                    {org.orgName}
                                </Option>
                            ))}
                        </Select>
                    </Space> */}
                    <Space direction="horizontal" className="w-full">
                        <Text strong><AccountBookOutlined /> Meta Ad Account</Text>
                        {loadingAccounts ? (
                            <Skeleton.Input active size="small" style={{ width: 300 }} />
                        ) : (
                            <Select
                                showSearch
                                placeholder="Choose an ad account"
                                optionFilterProp="label"
                                value={metaConnectStatus ? selectedAdAccount?.id : null}
                                onChange={handleAccountChange}
                                style={{ width: "100%", maxWidth: 400 }}
                                loading={loadingAccounts}
                                allowClear
                                options={adAccounts.map(acc => ({
                                    value: acc.id,
                                    label: `${acc.name} (${acc.id})`,
                                }))}
                                disabled={!metaConnectStatus}
                            />

                        )}
                    </Space>
                    <Button
                        type="primary"
                        icon={<SyncOutlined spin={syncing} />}
                        onClick={syncCampaigns}
                        disabled={syncing || !selectedAdAccount?.id || !metaConnectStatus}
                        className="bg-[#1F344C] hover:bg-[#1F000C]"
                    >
                        {syncing ? "Syncing..." : "Sync Campaigns"}
                    </Button>
                </div>
            </div>



            {/* Account Details */}
            {accountDetails && metaConnectStatus && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6 text-sm">
                    <div><strong>Meta Ad Account:</strong> {accountDetails.adAccountName}</div>
                    <div><strong>Meta Ad Account ID:</strong> {accountDetails.adAccountId}</div>
                    <div>
                        <strong>Status:</strong>{' '}
                        <Tag color={accountDetails.adAccountStatus === "Active" ? "green" : "gray"}>
                            {accountDetails.adAccountStatus}
                        </Tag>
                    </div>
                    <div><strong>Default:</strong> {accountDetails.isDefault}</div>
                </div>
            )}

            {accountDetails &&
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    <Select
                        showSearch
                        allowClear
                        placeholder="Search campaign by name"
                        style={{ width: "100%", maxWidth: 350 }}
                        onSearch={handleSearch}
                        onChange={(val) => handleSearch(val || "")}
                        filterOption={false}
                        notFoundContent={null}
                        options={searchData.map(c => ({
                            value: c.campaignName,
                            label: c.campaignName,
                        }))}
                    />
                    <Select
                        placeholder="Filter by status"
                        style={{ width: 200 }}
                        value={statusFilter}
                        onChange={handleStatusChange}
                    >
                        <Option value="ALL">All Status</Option>
                        <Option value="ACTIVE">Active</Option>
                        <Option value="PAUSED">Paused</Option>
                        <Option value="INACTIVE">Inactive</Option>
                        <Option value="DISABLED">Disabled</Option>
                    </Select>
                </div>
            }

            {/* Table */}
            <div className="p-4 bg-gray-50">
                {loadingCampaigns ? (
                    <Table
                        dataSource={[]}
                        columns={columns}
                        rowKey="id"
                        pagination={{ pageSize: 10, align: "center" }}
                        scroll={{ x: 'max-content' }}
                        loading={true}
                    />
                ) : (
                    metaConnectStatus ? <Table
                        dataSource={filteredCampaigns}
                        columns={columns}
                        rowKey="id"
                        pagination={{ pageSize: 10, align: "center" }}
                        scroll={{ x: 'max-content' }}
                        rowClassName={(record) => record.isTimesPanelCampaign ? 'bg-purple-50/30' : ''}
                    /> : <Table
                        dataSource={[]}
                        columns={columns}
                        rowKey="id"
                        pagination={{ pageSize: 10, align: "center" }}
                        scroll={{ x: 'max-content' }}
                    />
                )}
            </div>
        </CommonLayout>
    );
};

export default CampaignListing;